import React from "react";

import { useIntl } from "react-intl";

import LabeledInfo from "Components/LabeledInfo";
import { capitalize } from "Libs/utils";
import { useAutoscallingValues } from "src/organization/pages/project/environment/metrics/containers/Service/util";

import type { AppTree } from "src/organization/common/containers/ServicesDisplay/util/serviceGraph";

type AppInfosProps = {
  metadata: AppTree["metadata"];
  isSizingApiEnabled?: boolean;
  isServicesTab: boolean;
  cpu?: string;
  ram?: string;
  disk?: string;
  profileLabel?: string;
  crons?: number;
};

const AppInfos = ({
  metadata,
  isSizingApiEnabled,
  isServicesTab = false,
  cpu,
  ram,
  disk,
  profileLabel,
  crons
}: AppInfosProps) => {
  const intl = useIntl();
  const instancesCount = metadata.instancesCount;
  const { thresholdsEnabled } = useAutoscallingValues(metadata.appName);

  const formatLabel = (label: string) => {
    if (!label) {
      return intl.formatMessage({ id: "unknown" });
    }
    const splitLabel = label.split(":");
    if (splitLabel[0] === "php") {
      return splitLabel[0].toUpperCase() + " " + splitLabel[1];
    }
    return capitalize(splitLabel[0]) + " " + splitLabel[1];
  };

  const versionEl = (
    <LabeledInfo
      gridColumn="auto"
      label={intl.formatMessage({ id: "runtime" })}
      value={formatLabel(metadata?.type)}
      shouldCapitalize={false}
    />
  );

  const cronsEl = (
    <LabeledInfo
      gridColumn="auto"
      label={intl.formatMessage({ id: "crons_dialog" })}
      value={crons ?? intl.formatMessage({ id: "unknown" })}
    />
  );

  const profileEl = (
    <LabeledInfo
      gridColumn="auto"
      label={intl.formatMessage({ id: "profile_dialog" })}
      value={
        profileLabel?.toLocaleUpperCase() ??
        intl.formatMessage({ id: "unknown" })
      }
    />
  );

  const instancesEl = (
    <LabeledInfo
      gridColumn="auto"
      label={intl.formatMessage({ id: "instances_dialog" })}
      value={instancesCount ?? intl.formatMessage({ id: "unknown" })}
      hasAutoscaling={thresholdsEnabled}
      service={metadata.service}
      showAutoscalingDialog={isServicesTab}
    />
  );
  if (isSizingApiEnabled) {
    if (isServicesTab) {
      return (
        <>
          {versionEl}
          {profileEl}
          {instancesEl}
          {cronsEl}
        </>
      );
    }
    return (
      <>
        <LabeledInfo
          gridColumn="auto"
          label={intl.formatMessage({ id: "cpu_dialog" })}
          value={cpu ? `${cpu} CPU` : intl.formatMessage({ id: "unknown" })}
        />
        <LabeledInfo
          gridColumn="auto"
          label={intl.formatMessage({ id: "ram_dialog" })}
          value={ram ? `${ram} GB` : intl.formatMessage({ id: "unknown" })}
        />
        <LabeledInfo
          gridColumn="auto"
          label={intl.formatMessage({ id: "disk_dialog" })}
          value={`${disk} GB`}
        />
        {instancesEl}
        {profileEl}
        {cronsEl}
        <LabeledInfo
          gridColumn="auto"
          label={intl.formatMessage({ id: "autoscaling" })}
          value={
            thresholdsEnabled
              ? intl.formatMessage({ id: "enabled" })
              : intl.formatMessage({ id: "disabled" })
          }
          hasAutoscaling={thresholdsEnabled}
        />
      </>
    );
  }

  return (
    <>
      {versionEl}
      <LabeledInfo
        gridColumn="auto"
        label={intl.formatMessage({ id: "size" })}
        value={metadata?.size || intl.formatMessage({ id: "unknown" })}
      />
      <LabeledInfo
        gridColumn="auto"
        label={intl.formatMessage({ id: "disk" })}
        value={metadata?.disk || 0}
      />
      <LabeledInfo
        gridColumn="auto"
        label={intl.formatMessage({ id: "crons" })}
        value={metadata?.crons ? Object.keys(metadata.crons).length : 0}
      />
      {instancesCount && (
        <LabeledInfo
          gridColumn="auto"
          label={intl.formatMessage({ id: "instances" })}
          value={instancesCount}
        />
      )}
    </>
  );
};

export default AppInfos;
